import PropTypes from 'prop-types';
import React from 'react';
import _ from "lodash";

import {
  filterRidesByStatus,
  filterRidesByPlacedStatus,
  filterRidesByAlsoRan,
  filterRidesByCasualty,
  sortByFinishPosition,
  sortByClothNo
} from 'modules/HorseRacing/utils/rideFilter';

import RaceVideoPlayer from 'modules/HorseRacing/components/RaceVideoPlayer';
import HorseRacingRacecardResultRunner from "./HorseRacingRacecardResultRunner";
import HorseRacingRacecardResultNonRunner from './HorseRacingRacecardResultNonRunner';
import CaretLink from 'modules/Common/components/CaretLink';

import {convertCurrencyString} from "utils/CurrencyFormatting";

import {buildRuleFourString} from 'modules/HorseRacing/utils';

function hideNull(element) {
  return element ? element : '';
}

function bracket(element) {
  return element ? `(${element})` : '';
}

function transformData(runners) {
  const hn = hideNull;
  return (runners.map(runner => ({
    key : runner.ride_reference.id,
    status : hn(runner.ride_status),
    saddleClothNo: hn(runner.cloth_number),
    drawNumber: bracket(runner.draw_number),
    position : hn(runner.amended_position || runner.finish_position),
    casualty : hn(runner.casualty),
    finishDistance : hn(runner.finish_distance),
    form : hn(runner.horse.formsummary.display_text),
    formWatch: hn(runner.formwatch),
    horseName : hn(runner.horse.name),
    horseId : hn(runner.horse.horse_reference.id),
    horseLastRan:  hn(runner.horse.last_ran_days),
    horseAge : runner.horse.age,
    weight : hn(runner.handicap),
    rideDesc : hn(runner.ride_description),
    betMovements: runner.bet_movements,
    trainer : runner.trainer,
    jockey : runner.jockey,
    jockeyClaim : bracket(runner.jockey_claim),
    officialRating : hn(runner.official_rating),
    odds : hn(runner.betting.current_odds) + hn(runner.betting.favourite.betting_favourite),
    silkFilename : hn(runner.silk_filename)
  })))
}

export default function HorseRacingRacecardPostRace(props){
  const {
    race: {
      rides : raceRunners,
      videos: raceVideos,

      tote_win,
      place_win,
      exacta_win,
      trifecta,
      straight_forecast,
      tricast,
      swingers,
      deductions = []
    }
  } = props;

  const orderedByClothRaceRunners = raceRunners.sort(sortByClothNo);
  const runners = filterRidesByStatus(raceRunners, "RUNNER");
  const nonRunners = filterRidesByStatus(orderedByClothRaceRunners, "WITHDRAWN", "NONRUNNER");

  const placedRunners = filterRidesByPlacedStatus(runners).sort(sortByFinishPosition);
  const alsoRanRunners = filterRidesByAlsoRan(runners).sort(sortByClothNo);
  // Any horse who was a runner but had a finish position of 0 did not finish.
  const casualties = filterRidesByCasualty(runners).sort(sortByClothNo);

  const hasToteBreakdown = !!(tote_win || place_win);
  const hasOtherBettingBreakdown = !!(exacta_win || trifecta || straight_forecast || tricast || swingers);
  const hasBettingBreakdown = hasToteBreakdown || hasOtherBettingBreakdown;

  const meetingProvider = props.provider ? props.provider.toLowerCase() : 'unknown';

  if (!props.isComplete) {
    return <div className="hr-result-race-not-complete">
      <p>There is no result yet for this race.</p>
      <p><CaretLink to={`/racing/racecards/${props.params.date}/${props.params.courseSlug}/racecard/${props.params.raceId}/${props.params.raceSlug}`}>View the race card.</CaretLink></p>
    </div>;
  }
  
  return (
    <section>
      {
        transformData(placedRunners).map((runner) => {
          return <HorseRacingRacecardResultRunner runner={runner} key={runner.key}/>
        })
      }
      {
        !_.isEmpty(alsoRanRunners) &&
          <section>
            <div className="hr-racing-racecard-section-header">
              <h4>Also Ran</h4>
            </div>
            {
              transformData(alsoRanRunners).map(alsoRan => {
                return <HorseRacingRacecardResultRunner runner={alsoRan} key={alsoRan.key}/>
              })
            }
          </section>
      }
      {
        !_.isEmpty(casualties) &&
          <section>
            <div className="hr-racing-racecard-section-header">
              <h4>Did Not Finish</h4>
            </div>
            {
              transformData(casualties).map(casualty => {
                return <HorseRacingRacecardResultRunner runner={casualty} key={casualty.key}/>
              })
            }
          </section>
      }
      {
        !_.isEmpty(nonRunners) &&
          <section>
            <div className="hr-racing-racecard-section-header">
              <h4>Non-Runners</h4>
            </div>
            {
              transformData(nonRunners).map(nonRunner => {
                return <HorseRacingRacecardResultNonRunner nonRunner={nonRunner} key={nonRunner.key}/>
              })
            }
          </section>
      }
      {
        hasBettingBreakdown && <section className="hr-racing-racecard-betting-breakdown">
          <div className="hr-racing-racecard-section-header">
            <h4>Betting Breakdown</h4>
          </div>
          {
            hasToteBreakdown && <div className="hr-racing-racecard-betting-breakdown-content">
              <OptionalData value={convertCurrencyString(tote_win)}>Tote Win:</OptionalData>
              <OptionalData value={convertCurrencyString(place_win)}>Pl:</OptionalData>
            </div>
          }
          {
            hasOtherBettingBreakdown && <div className="hr-racing-racecard-betting-breakdown-content">
              <OptionalData value={convertCurrencyString(exacta_win)}>EX:</OptionalData>
              <OptionalData value={convertCurrencyString(trifecta)}>Trifecta:</OptionalData>
              <OptionalData value={convertCurrencyString(straight_forecast)}>SF:</OptionalData>
              <OptionalData value={convertCurrencyString(tricast)}>Tricast:</OptionalData>
              <OptionalData value={convertCurrencyString(swingers)}>Swingers:</OptionalData>
            </div>
          }
        </section>
      }
      {
        (deductions && deductions.length > 0) &&
        <section className="hr-racing-racecard-rule-four">
          <div className="hr-racing-racecard-section-header">
            <h4>Rule 4</h4>
          </div>
          <div className="hr-racing-racecard-rule-four-content">
            {deductions.map((deduction, index) => (<p key={index} >{buildRuleFourString(deduction)}</p>))}
          </div>
        </section>
      }
      {
        (raceVideos && raceVideos.length > 0 || meetingProvider === 'atr') &&
        <section>
          <div className="video-replay-wrapper">
            <h4 className="video-replay-title">Video Replay</h4>
            <RaceVideoPlayer videos={raceVideos} provider={meetingProvider} />
          </div>
        </section>
      }
    </section>
  );
}

HorseRacingRacecardPostRace.propTypes = {
  isComplete: PropTypes.bool,
  params: PropTypes.object,
  race : PropTypes.object.isRequired,
  meeting: PropTypes.object,
  meetings: PropTypes.object,
  provider: PropTypes.string
};

function OptionalData(props) {
  return props.value
   ? (
       <span>
         <span className="hr-racing-racecard-betting-breakdown-content-label">{props.children}</span>
         <span className="hr-racing-racecard-betting-breakdown-content-value">{props.value}</span>
       </span>
     )
   : null;
}
OptionalData.propTypes = {
  value: PropTypes.any,
  children: PropTypes.node
};



// WEBPACK FOOTER //
// ./modules/HorseRacing/components/HorseRacingRacecardPostRace.js